import React, { useEffect, useRef } from 'react';

function TableGamesCumulativeChart({ d3, data }) {
  const containerRef = useRef(null);

  useEffect(() => {
    if (!d3 || !containerRef.current || !data?.length) return;

    const container = containerRef.current;
    container.innerHTML = '';

    const margin = { top: 10, right: 80, bottom: 50, left: 80 };
    const parent = container.parentElement;
    const cw =
      container.clientWidth ||
      (parent && parent.clientWidth) ||
      container.getBoundingClientRect().width ||
      600;
    const width = cw - margin.left - margin.right;
    const height = Math.round(width * 0.6);

    const isSmall = cw < 600;
    const formatCurrency = (d) => {
      const abs = Math.abs(d);
      if (isSmall) {
        const s = d3.format('~s')(abs).replace('G', 'B');
        return d < 0 ? `-$${s}` : `$${s}`;
      }
      const full = d3.format(',')(abs);
      return d < 0 ? `-$${full}` : `$${full}`;
    };

    const x = d3
      .scaleTime()
      .domain(d3.extent(data, (d) => d.date))
      .range([0, width]);

    const y = d3
      .scaleLinear()
      .domain(d3.extent(data, (d) => d.cumulative))
      .nice()
      .range([height, 0]);

    const svg = d3
      .select(container)
      .append('svg')
      .attr('viewBox', `0 0 ${width + margin.left + margin.right} ${height + margin.top + margin.bottom}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .attr('width', width + margin.left + margin.right)
      .attr('height', height + margin.top + margin.bottom)
      .style('width', '100%')
      .style('height', '100%');

    const chartGroup = svg
      .append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    const clipId = 'tgcum-clip';
    svg.append('defs')
      .append('clipPath')
      .attr('id', clipId)
      .append('rect')
      // clip coordinates are relative to chartGroup (already translated by margins)
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', width)
      .attr('height', height);

    const plotGroup = chartGroup.append('g').attr('clip-path', `url(#${clipId})`);

    const xAxis = d3.axisBottom(x).tickFormat(d3.timeFormat('%b %d')).ticks(3);
    const yAxis = d3.axisLeft(y).tickFormat(formatCurrency);

    chartGroup
      .append('g')
      .attr('transform', `translate(0,${height})`)
      .attr('class', 'x-axis')
      .call(xAxis)
      .selectAll('text')
      .style('font-size', '16px');

    chartGroup
      .append('g')
      .attr('class', 'y-axis')
      .call(yAxis)
      .selectAll('text')
      .style('font-size', '16px');

    const areaGenerator = d3
      .area()
      .x((d) => x(d.date))
      .y0(y(0))
      .y1((d) => y(d.cumulative));

    const lineGenerator = d3
      .line()
      .x((d) => x(d.date))
      .y((d) => y(d.cumulative));

    const areaPath = plotGroup
      .append('path')
      .datum(data)
      .attr('fill', '#76ace0')
      .attr('opacity', 0.4)
      .attr('d', areaGenerator);

    const linePath = plotGroup
      .append('path')
      .datum(data)
      .attr('fill', 'none')
      .attr('stroke', '#76ace0')
      .attr('stroke-width', 2)
      .attr('d', lineGenerator);

    const circles = plotGroup
      .selectAll('circle')
      .data(data)
      .enter()
      .append('circle')
      .attr('cx', (d) => x(d.date))
      .attr('cy', (d) => y(d.cumulative))
      .attr('r', 3)
      .attr('fill', '#76ace0');

    circles.append('title').text((d) => `Date: ${d3.timeFormat('%b %d, %Y')(d.date)}\nCumulative: ${d3.format(',')(d.cumulative)}`);

    const dataMin = d3.min(data, (d) => d.date);
    const dataMax = d3.max(data, (d) => d.date);

    const zoom = d3
      .zoom()
      .scaleExtent([1, 10])
      .extent([
        [margin.left, margin.top],
        [margin.left + width, margin.top + height],
      ])
      .translateExtent([
        [margin.left + x(dataMin), margin.top],
        [margin.left + x(dataMax), margin.top + height],
      ])
      .on('zoom', (event) => {
        let t = event.transform;
        if (t.y !== 0) {
          t = d3.zoomIdentity.translate(t.x, 0).scale(t.k);
          zoomPane.call(zoom.transform, t);
          return;
        }
        const newX = t.rescaleX(x);
        const newY = y; // keep Y fixed

        chartGroup
          .select('.x-axis')
          .call(d3.axisBottom(newX).ticks(3).tickFormat(d3.timeFormat('%b %d, %Y')))
          .selectAll('text')
          .style('font-size', '16px');

        // y-axis remains unchanged

        areaPath.attr(
          'd',
          areaGenerator
            .x((d) => newX(d.date))
            .y0(y(0))
            .y1((d) => newY(d.cumulative))
        );
        linePath.attr(
          'd',
          lineGenerator
            .x((d) => newX(d.date))
            .y((d) => newY(d.cumulative))
        );

        plotGroup
          .selectAll('circle')
          .attr('cx', (d) => newX(d.date))
          .attr('cy', (d) => newY(d.cumulative));
      });

    const zoomPane = svg
      .append('rect')
      .attr('width', width)
      .attr('height', height)
      .attr('x', margin.left)
      .attr('y', margin.top)
      .style('fill', 'none')
      .style('pointer-events', 'all')
      .call(zoom);
  }, [d3, data]);

  return (
    <div className="chart-card">
      <h2>TABLE GAME STATS</h2>
      <div ref={containerRef} />
    </div>
  );
}

export default TableGamesCumulativeChart;
